import { createSlice, current } from '@reduxjs/toolkit';
import {
	getBreachRiskClassificationList,
	getBreachRiskClassification,
	addNewBreachRiskClassification,
	updateBreachRiskClassification,
	deleteBreachRiskClassification,
	checkBreachRiskClassificationExists,
} from './thunk';

const getOptionLabel = item => item?.name || item?.label || item?.description || item?._id || '';

export const initialState = {
	breachRiskClassificationList: [],
	breachRiskClassificationListSelect: [],
	isLoadingList: false,
	isSuccess: false,
	isAdding: false,
	isAdded: false,
	isAddError: false,
	isUpdating: false,
	isUpdated: false,
	isUpdateError: false,
	isDeleting: false,
	isDeleted: false,
	isDeleteError: false,
	requireCloseModal: false,
	exists: false,
};

const BreachRiskClassificationSlice = createSlice({
	name: 'BreachRiskClassification',
	initialState,
	reducers: {
		setRequireCloseModal(state, action) {
			state.requireCloseModal = action.payload;
		},
	},
	extraReducers: builder => {
		// List
		builder.addCase(getBreachRiskClassificationList.pending, state => {
			state.isLoadingList = true;
			state.isSuccess = false;
		});

		builder.addCase(getBreachRiskClassificationList.fulfilled, (state, action) => {
			const list = action.payload || [];
			state.breachRiskClassificationList = list;
			state.breachRiskClassificationListSelect = list.map(item => ({
				item,
				value: item._id,
				label: getOptionLabel(item),
			}));
			state.isLoadingList = false;
			state.isSuccess = true;
		});

		builder.addCase(getBreachRiskClassificationList.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isLoadingList = false;
			state.isSuccess = false;
		});

		// Add
		builder.addCase(addNewBreachRiskClassification.pending, state => {
			state.isAdding = true;
			state.isAdded = false;
			state.isAddError = false;
		});

		builder.addCase(addNewBreachRiskClassification.fulfilled, (state, action) => {
			state.breachRiskClassificationList.push(action.payload);
			state.breachRiskClassificationListSelect.push({
				item: action.payload,
				value: action.payload._id,
				label: getOptionLabel(action.payload),
			});
			state.isAdding = false;
			state.isAdded = true;
			state.requireCloseModal = true;
		});

		builder.addCase(addNewBreachRiskClassification.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isAdding = false;
			state.isAddError = true;
		});

		// Update
		builder.addCase(updateBreachRiskClassification.pending, state => {
			state.isUpdating = true;
			state.isUpdated = false;
			state.isUpdateError = false;
		});

		builder.addCase(updateBreachRiskClassification.fulfilled, (state, action) => {
			state.breachRiskClassificationList = state.breachRiskClassificationList.map(item =>
				item._id.toString() === action.payload._id.toString()
					? { ...item, ...action.payload }
					: item
			);
			state.breachRiskClassificationListSelect =
				state.breachRiskClassificationListSelect.map(opt =>
					opt.value.toString() === action.payload._id.toString()
						? {
							item: action.payload,
							value: action.payload._id,
							label: getOptionLabel(action.payload),
						}
						: opt
				);
			state.isUpdating = false;
			state.isUpdated = true;
			state.requireCloseModal = true;
		});

		builder.addCase(updateBreachRiskClassification.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isUpdating = false;
			state.isUpdateError = true;
		});

		// Delete
		builder.addCase(deleteBreachRiskClassification.pending, state => {
			state.isDeleting = true;
			state.isDeleted = false;
			state.isDeleteError = false;
		});

		builder.addCase(deleteBreachRiskClassification.fulfilled, (state, action) => {
			state.breachRiskClassificationList = current(
				state.breachRiskClassificationList
			).filter(item => item._id !== action.payload._id);
			state.breachRiskClassificationListSelect = current(
				state.breachRiskClassificationListSelect
			).filter(opt => opt.value !== action.payload._id);
			state.isDeleting = false;
			state.isDeleted = true;
			state.requireCloseModal = true;
		});

		builder.addCase(deleteBreachRiskClassification.rejected, (state, action) => {
			state.error = action.payload || null;
			state.isDeleting = false;
			state.isDeleteError = true;
		});

		// Check exists
		builder.addCase(checkBreachRiskClassificationExists.pending, state => {
			state.exists = false;
		});

		builder.addCase(checkBreachRiskClassificationExists.fulfilled, (state, action) => {
			state.exists = action.payload.exists;
		});

		builder.addCase(checkBreachRiskClassificationExists.rejected, state => {
			state.exists = false;
		});
	},
});

export const { setRequireCloseModal } = BreachRiskClassificationSlice.actions;

export default BreachRiskClassificationSlice.reducer;
